import React, { useState, useEffect } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useChat } from "../../contexts/ChatContext";
import { useProposal } from "../../contexts/ProposalContext";
import { formatDistanceToNow } from "date-fns";
import clsx from "clsx";
import NewChatModal from "./NewChatModal";
import Modal from "../common/Modal";

const ChatSidebar = ({
  isOpen,
  onClose,
  onNewChat,
  currentSessionId,
  proposalId = null, // NEW: Filter to specific proposal if provided
  onSessionSelect, // NEW: Callback for when a session is selected
}) => {
  const {
    sessions,
    sessionsLoading,
    getGeneralSessions,
    getProposalSessions,
    updateSession,
    deleteSession,
  } = useChat();

  const { proposals } = useProposal();
  const location = useLocation();
  const params = useParams();

  const [activeDropdown, setActiveDropdown] = useState(null);
  const [editingSession, setEditingSession] = useState(null);
  const [newSessionName, setNewSessionName] = useState("");
  const [showNewChatModal, setShowNewChatModal] = useState(false);

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);

  const [filteredSessions, setFilteredSessions] = useState({
    generalSessions: [],
    proposalSessions: [],
  });

  useEffect(() => {
    if (proposalId) {
      setFilteredSessions({
        proposalSessions: getProposalSessions(proposalId),
        generalSessions: [],
      });
    } else {
      setFilteredSessions({
        proposalSessions: getProposalSessions(),
        generalSessions: getGeneralSessions(),
      });
    }
  }, [sessions, proposalId]);

  // Determine if we're in workspace mode
  const isInWorkspace = location.pathname.includes("/workspace/");
  const workspaceProposalId = params.proposalId;

  // Get the sessions to display based on context
  const getSessionsToDisplay = () => {
    if (proposalId) {
      // In workspace mode: only show sessions for this proposal
      return getProposalSessions(proposalId);
    } else {
      // In general chat mode: show all sessions
      const generalSessions = getGeneralSessions();
      const proposalSessions = getProposalSessions();
      return { generalSessions, proposalSessions };
    }
  };

  const sessionsData = getSessionsToDisplay();

  // If we're in proposal mode, sessionsData is an array
  // If we're in general mode, it's an object with generalSessions and proposalSessions
  const isProposalMode = proposalId !== null;
  const displaySessions = isProposalMode
    ? filteredSessions.proposalSessions
    : null;
  const generalSessions = isProposalMode
    ? []
    : filteredSessions.generalSessions;
  const proposalSessions = isProposalMode
    ? []
    : filteredSessions.proposalSessions;

  const handleSessionAction = async (action, sessionId, data = {}) => {
    setActiveDropdown(null);

    switch (action) {
      case "rename":
        setEditingSession(sessionId);
        const session = sessions.find((s) => s.session_id === sessionId);
        setNewSessionName(session?.session_name || "");
        break;

      case "save-rename":
        if (newSessionName.trim()) {
          await updateSession(sessionId, {
            session_name: newSessionName.trim(),
          });
        }
        setEditingSession(null);
        setNewSessionName("");
        break;

      case "cancel-rename":
        setEditingSession(null);
        setNewSessionName("");
        break;

      case "pin":
        await updateSession(sessionId, {
          is_pinned: !data.isPinned,
        });
        break;

      case "delete":
        // Instead of window.confirm, set up modal
        const sessionToDelete = sessions.find(
          (s) => s.session_id === sessionId
        );
        setSessionToDelete(sessionToDelete);
        setShowDeleteModal(true);
        break;

      default:
        break;
    }
  };

  const handleConfirmDelete = async () => {
    if (sessionToDelete) {
      await deleteSession(sessionToDelete.session_id);
      setShowDeleteModal(false);
      setSessionToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSessionToDelete(null);
  };

  const formatLastActivity = (timestamp) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return "Unknown";
    }
  };

  // New chat handlers
  const handleNewChatClick = () => {
    if (isProposalMode || isInWorkspace) {
      // In proposal mode or workspace mode, create proposal-specific chat directly
      const targetProposalId = proposalId || workspaceProposalId;
      onNewChat?.(parseInt(targetProposalId));
    } else {
      // In general mode, show modal
      setShowNewChatModal(true);
    }
  };

  const handleCreateGeneralChat = () => {
    onNewChat?.();
  };

  const handleCreateProposalChat = (selectedProposalId) => {
    onNewChat?.(selectedProposalId);
  };

  // NEW: Handle session click
  const handleSessionClick = (session, e) => {
    e.preventDefault();

    if (isInWorkspace && onSessionSelect) {
      // If we're in workspace mode and have a session select callback, use it
      onSessionSelect(session);
      onClose?.();
    } else {
      // Otherwise, navigate to the old chat interface
      window.location.href = `/chat/${session.session_id}`;
    }
  };

  const SessionItem = ({ session, showProposalInfo = false }) => {
    const isActive = session.session_id === currentSessionId;
    const isEditing = editingSession === session.session_id;
    const proposal =
      showProposalInfo && session.proposal_id
        ? proposals?.find((p) => p.proposal_id === session.proposal_id)
        : null;

    return (
      <div
        className={clsx(
          "group relative rounded-lg border transition-all duration-200",
          {
            "bg-primary-50 border-primary-200 shadow-sm": isActive,
            "bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm":
              !isActive,
          }
        )}
      >
        {/* Use button instead of Link for workspace mode, Link for chat mode */}
        {isInWorkspace && onSessionSelect ? (
          <button
            onClick={(e) => handleSessionClick(session, e)}
            className="block p-3 w-full text-left"
          >
            <SessionContent
              session={session}
              proposal={proposal}
              isActive={isActive}
              isEditing={isEditing}
            />
          </button>
        ) : (
          <Link
            to={`/chat/${session.session_id}`}
            onClick={onClose}
            className="block p-3"
          >
            <SessionContent
              session={session}
              proposal={proposal}
              isActive={isActive}
              isEditing={isEditing}
            />
          </Link>
        )}

        {/* Session actions dropdown */}
        {!isEditing && activeDropdown === session.session_id && (
          <div className="absolute right-0 top-10 w-40 bg-white border border-slate-200 rounded-lg shadow-lg z-10">
            <button
              onClick={() => handleSessionAction("rename", session.session_id)}
              className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-t-lg"
            >
              <FontAwesomeIcon icon="edit" className="mr-2 text-xs" />
              Rename
            </button>
            <button
              onClick={() =>
                handleSessionAction("pin", session.session_id, {
                  isPinned: session.is_pinned,
                })
              }
              className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
            >
              <FontAwesomeIcon
                icon={Boolean(session.is_pinned) ? "bookmark" : "bookmark"}
                className={clsx(
                  "mr-2 text-xs",
                  Boolean(session.is_pinned) ? "text-amber-500" : ""
                )}
              />
              {Boolean(session.is_pinned) ? "Unpin" : "Pin"}
            </button>
            <button
              onClick={() => handleSessionAction("delete", session.session_id)}
              className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-b-lg"
            >
              <FontAwesomeIcon icon="trash" className="mr-2 text-xs" />
              Delete
            </button>
          </div>
        )}
      </div>
    );
  };

  // Extracted session content to avoid duplication
  const SessionContent = ({ session, proposal, isActive, isEditing }) => (
    <div className="flex items-start">
      <div className="flex-1 min-w-0 mr-2">
        {isEditing ? (
          <input
            type="text"
            value={newSessionName}
            onChange={(e) => setNewSessionName(e.target.value)}
            onBlur={() =>
              handleSessionAction("save-rename", session.session_id)
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSessionAction("save-rename", session.session_id);
              } else if (e.key === "Escape") {
                handleSessionAction("cancel-rename", session.session_id);
              }
            }}
            className="w-full text-sm font-medium bg-white border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500"
            autoFocus
            onClick={(e) => e.preventDefault()}
          />
        ) : (
          <h4
            className={clsx(
              "text-sm font-medium truncate",
              isActive ? "text-primary-900" : "text-slate-900"
            )}
          >
            <span className="flex items-center">
              {Boolean(session.is_pinned) && (
                <FontAwesomeIcon
                  icon="bookmark"
                  className="mr-1 text-amber-500"
                />
              )}
              <span>{session.session_name}</span>
            </span>
          </h4>
        )}

        {proposal && (
          <p className="text-xs text-slate-600 truncate mt-1">
            {proposal.proposal_name}
          </p>
        )}

        <div className="flex items-center space-x-2 mt-1">
          <span className="text-xs text-slate-500">
            {formatLastActivity(session.last_activity)}
          </span>
          {session.message_count > 0 && (
            <span className="text-xs text-slate-400">
              • {session.message_count} messages
            </span>
          )}
        </div>
      </div>

      {/* Icons container - session type + options button together */}
      <div className="flex items-center space-x-1 flex-shrink-0">
        {/* Session type icon with color coding */}
        <div
          className={clsx(
            "p-1 rounded",
            session.session_type === "proposal_specific"
              ? "bg-orange-100 text-orange-600"
              : "bg-emerald-100 text-emerald-600"
          )}
        >
          <FontAwesomeIcon
            icon={
              session.session_type === "proposal_specific" ? "file" : "robot"
            }
            className="text-xs"
          />
        </div>

        {/* Options button */}
        {!isEditing && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setActiveDropdown(
                activeDropdown === session.session_id
                  ? null
                  : session.session_id
              );
            }}
            className="px-2 py-1 rounded hover:bg-slate-200 transition-colors duration-200"
            title="More options"
          >
            <FontAwesomeIcon
              icon="ellipsis-v"
              className="text-slate-400 hover:text-slate-600 text-sm"
            />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={clsx(
          "w-80 bg-slate-50 border-r border-slate-200 flex flex-col transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0",
          {
            "translate-x-0": isOpen,
            "-translate-x-full": !isOpen,
            "fixed inset-y-0 left-0 z-50 lg:static": true,
          }
        )}
      >
        {/* Header */}
        <div className="p-4 border-b border-slate-200 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-900">
              {isProposalMode ? "Proposal Chats" : "Chat History"}
            </h2>
            <button
              onClick={onClose}
              className="lg:hidden p-1 rounded-lg hover:bg-slate-100 transition-colors duration-200"
            >
              <FontAwesomeIcon icon="times" className="text-slate-500" />
            </button>
          </div>

          {/* New Chat Button */}
          <button
            onClick={handleNewChatClick}
            className={clsx(
              "w-full flex items-center justify-center space-x-2 py-3 px-4 text-white rounded-lg transition-all duration-200 shadow-sm hover:shadow-md font-medium",
              {
                // Orange for workspace/proposal mode, green for general mode
                "bg-orange-500 hover:bg-orange-600":
                  isProposalMode || isInWorkspace,
                "bg-emerald-500 hover:bg-emerald-600":
                  !isProposalMode && !isInWorkspace,
              }
            )}
          >
            <FontAwesomeIcon icon="plus" />
            <span>
              {isProposalMode || isInWorkspace
                ? "New Proposal Chat"
                : "New Chat"}
            </span>
          </button>
        </div>

        {/* Sessions list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {sessionsLoading ? (
            <div className="flex items-center justify-center py-8">
              <FontAwesomeIcon
                icon="spinner"
                className="animate-spin text-slate-400"
              />
            </div>
          ) : isProposalMode ? (
            /* Proposal Mode: Show only proposal sessions */
            <div>
              {displaySessions.length > 0 ? (
                <div className="space-y-2">
                  {displaySessions
                    .sort((a, b) => {
                      // Sort pinned first, then by last activity
                      if (a.is_pinned !== b.is_pinned) {
                        return b.is_pinned - a.is_pinned;
                      }
                      return (
                        new Date(b.last_activity) - new Date(a.last_activity)
                      );
                    })
                    .map((session) => (
                      <SessionItem key={session.session_id} session={session} />
                    ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FontAwesomeIcon
                    icon="comment"
                    className="text-4xl text-slate-300 mb-3"
                  />
                  <p className="text-slate-500 text-sm mb-4">
                    No chat sessions for this proposal yet.
                  </p>
                  <button
                    onClick={handleNewChatClick}
                    className="text-sm text-primary-600 hover:text-primary-800"
                  >
                    Start your first conversation!
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* General Mode: Show categorized sessions */
            <>
              {/* PROPOSAL SESSIONS FIRST */}
              {proposalSessions.length > 0 && (
                <div>
                  <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">
                    Proposal Chats ({proposalSessions.length})
                  </h3>
                  <div className="space-y-2">
                    {proposalSessions
                      .sort((a, b) => {
                        // Sort pinned first, then by last activity
                        if (a.is_pinned !== b.is_pinned) {
                          return b.is_pinned - a.is_pinned;
                        }
                        return (
                          new Date(b.last_activity) - new Date(a.last_activity)
                        );
                      })
                      .map((session) => (
                        <SessionItem
                          key={session.session_id}
                          session={session}
                          showProposalInfo={true}
                        />
                      ))}
                  </div>
                </div>
              )}

              {/* GENERAL SESSIONS SECOND */}
              {generalSessions.length > 0 && (
                <div>
                  <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">
                    General Chats ({generalSessions.length})
                  </h3>
                  <div className="space-y-2">
                    {generalSessions
                      .sort((a, b) => {
                        // Sort pinned first, then by last activity
                        if (a.is_pinned !== b.is_pinned) {
                          return b.is_pinned - a.is_pinned;
                        }
                        return (
                          new Date(b.last_activity) - new Date(a.last_activity)
                        );
                      })
                      .map((session) => (
                        <SessionItem
                          key={session.session_id}
                          session={session}
                        />
                      ))}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {sessions.length === 0 && (
                <div className="text-center py-8">
                  <FontAwesomeIcon
                    icon="comment"
                    className="text-4xl text-slate-300 mb-3"
                  />
                  <p className="text-slate-500 text-sm">
                    No chat sessions yet.
                    <br />
                    Click "New Chat" to get started!
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* New Chat Modal - Only for general mode, not workspace */}
      {!isProposalMode && !isInWorkspace && (
        <NewChatModal
          isOpen={showNewChatModal}
          onClose={() => setShowNewChatModal(false)}
          onCreateGeneralChat={handleCreateGeneralChat}
          onCreateProposalChat={handleCreateProposalChat}
          proposals={proposals}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && sessionToDelete && (
        <Modal
          title="Delete Chat Session"
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonColor="red"
          widthClassName="max-w-md"
        >
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <FontAwesomeIcon icon="trash" className="text-red-600" />
              </div>
              <div className="flex-1">
                <p className="text-slate-900 font-medium">
                  Are you sure you want to delete this chat session?
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  "{sessionToDelete.session_name}"
                </p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <FontAwesomeIcon
                  icon="exclamation-triangle"
                  className="text-amber-600 text-sm mt-0.5 flex-shrink-0"
                />
                <div>
                  <p className="text-sm text-amber-800 font-medium">
                    This action cannot be undone
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    All messages in this chat session will be permanently
                    deleted.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Click outside to close dropdown */}
      {activeDropdown && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => setActiveDropdown(null)}
        />
      )}
    </>
  );
};

export default ChatSidebar;
